'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label } from 'recharts';
import { Stock } from '@/types';
import { formatNumber } from '@/lib/formatNumber';
import { useChartColors } from '@/lib/useChartColors';
import { PortfolioTreemap } from './PortfolioTreemap';
import { getApiBase } from '@/lib/apiBase';

interface PortfolioChartProps {
  stocks: Stock[];
  currency: 'USD' | 'CAD';
  exchangeRate: number;
  onSelectStock?: (stock: Stock) => void;
}

type TimeRange = '1D' | '1W' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | '2Y' | '5Y' | '10Y';
type ViewMode = 'profit-loss' | 'portfolio-value';
type DisplayMode = 'chart' | 'treemap';

type IntradayPoint = { timestamp: string; price: number };
type IntradayData = Record<string, IntradayPoint[]>;

export const PortfolioChart: React.FC<PortfolioChartProps> = ({ stocks, currency, exchangeRate, onSelectStock }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('1M');
  const [viewMode, setViewMode] = useState<ViewMode>('profit-loss');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('chart');
  const [intradayData, setIntradayData] = useState<IntradayData | null>(null);
  const [isLoadingIntraday, setIsLoadingIntraday] = useState(false);
  const chartColors = useChartColors();

  const fetchIntradayData = useCallback(async () => {
    if (stocks.length === 0) return;
    setIsLoadingIntraday(true);
    try {
      const res = await fetch(`${getApiBase()}/api/stocks/intraday?stocks=all`);
      if (res.ok) {
        const data: IntradayData = await res.json();
        setIntradayData(data);
      }
    } catch (err) {
      console.error('Failed to fetch intraday data:', err);
    } finally {
      setIsLoadingIntraday(false);
    }
  }, [stocks.length]);

  useEffect(() => {
    if (timeRange === '1D') {
      fetchIntradayData();
    }
  }, [timeRange, fetchIntradayData]);

  // Build intraday chart data for 1D mode
  const intradayChartData = useMemo(() => {
    if (timeRange !== '1D' || !intradayData || stocks.length === 0) return [];

    // Collect all unique timestamps across all stocks
    const allTimestamps = new Set<string>();
    Object.values(intradayData).forEach(points => {
      points.forEach(p => allTimestamps.add(p.timestamp));
    });

    const sortedTimestamps = Array.from(allTimestamps).sort();

    // Filter to regular market hours only (9:30 AM - 4:00 PM ET)
    const etTimeFormat = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const filteredTimestamps = sortedTimestamps.filter(ts => {
      const parts = etTimeFormat.formatToParts(new Date(ts));
      const hour = parseInt(parts.find(p => p.type === 'hour')!.value, 10);
      const minute = parseInt(parts.find(p => p.type === 'minute')!.value, 10);
      const totalMinutes = hour * 60 + minute;
      return totalMinutes >= 570 && totalMinutes <= 960; // 9:30=570, 16:00=960
    });

    // Build a price map per symbol for quick lookup
    const priceMaps: Record<string, Map<string, number>> = {};
    for (const [sym, points] of Object.entries(intradayData)) {
      const map = new Map<string, number>();
      points.forEach(p => map.set(p.timestamp, p.price));
      priceMaps[sym] = map;
    }

    return filteredTimestamps.map(ts => {
      let totalProfitLoss = 0;
      let totalPortfolioValue = 0;
      const tsTime = new Date(ts).getTime();

      stocks.forEach(stock => {
        const priceMap = priceMaps[stock.symbol];
        let price: number | undefined;

        if (priceMap) {
          price = priceMap.get(ts);
          // Forward-fill: find most recent price before this timestamp
          if (price === undefined) {
            const stockPoints = intradayData[stock.symbol];
            if (stockPoints) {
              for (let i = stockPoints.length - 1; i >= 0; i--) {
                if (new Date(stockPoints[i].timestamp).getTime() <= tsTime) {
                  price = stockPoints[i].price;
                  break;
                }
              }
            }
          }
        }

        if (price !== undefined) {
          stock.lots.forEach(lot => {
            const priceInUSD = stock.currency === 'CAD' ? price / exchangeRate : price;
            const lotPriceInUSD = lot.currency === 'CAD' ? lot.price / exchangeRate : lot.price;
            const plPerShareUSD = priceInUSD - lotPriceInUSD;
            const plPerShare = currency === 'CAD' ? plPerShareUSD * exchangeRate : plPerShareUSD;
            const priceInDisplay = currency === 'CAD' ? priceInUSD * exchangeRate : priceInUSD;

            totalProfitLoss += plPerShare * lot.shares;
            totalPortfolioValue += priceInDisplay * lot.shares;
          });
        }
      });

      return {
        date: new Date(ts).getTime(), // epoch ms for number axis
        profitLoss: totalProfitLoss,
        portfolioValue: totalPortfolioValue,
      };
    });
  }, [timeRange, intradayData, stocks, currency, exchangeRate]);

  // Build daily chart data for non-1D modes
  const dailyChartData = useMemo(() => {
    if (timeRange === '1D' || stocks.length === 0) return [];

    // 1. Collect all unique dates from all stocks
    const allDates = new Set<string>();
    stocks.forEach(stock => {
      stock.historicalData.forEach(point => allDates.add(point.date));
    });

    // 2. Sort dates
    const sortedDates = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // 3. Determine start date based on timeRange
    const now = new Date();
    let startDate: Date;

    switch (timeRange) {
      case '1W': {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 10); // 10 calendar days to capture ~5 trading days
        break;
      }
      case '1M': {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      }
      case '3M': {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        break;
      }
      case '6M': {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 6);
        break;
      }
      case 'YTD': {
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      }
      case '1Y': {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      }
      case '2Y': {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 2);
        break;
      }
      case '5Y': {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 5);
        break;
      }
      case '10Y': {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 10);
        break;
      }
      default:
        startDate = new Date(now);
        break;
    }

    // Set to start of day to avoid timezone issues
    startDate.setHours(0, 0, 0, 0);

    // Filter dates using timestamp comparison to avoid timezone issues
    const filteredDates = sortedDates.filter(d => {
      const dateObj = new Date(d + 'T00:00:00');
      return dateObj.getTime() >= startDate.getTime();
    });

    // 4. Build data points
    return filteredDates.map(date => {
      let totalProfitLoss = 0;
      let totalPortfolioValue = 0;

      stocks.forEach(stock => {
        let point = stock.historicalData.find(p => p.date === date);
        if (!point) {
          const previousPoints = stock.historicalData
            .filter(p => p.date < date)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          point = previousPoints[0];
        }

        if (point) {
          stock.lots.forEach(lot => {
            const historicalPriceInUSD = stock.currency === 'CAD' ? point.price / exchangeRate : point.price;
            const lotPriceInUSD = lot.currency === 'CAD' ? lot.price / exchangeRate : lot.price;
            const profitLossPerShareUSD = historicalPriceInUSD - lotPriceInUSD;
            const profitLossPerShare = currency === 'CAD'
              ? profitLossPerShareUSD * exchangeRate
              : profitLossPerShareUSD;
            const historicalPriceInDisplayCurrency = currency === 'CAD'
              ? historicalPriceInUSD * exchangeRate
              : historicalPriceInUSD;

            totalProfitLoss += profitLossPerShare * lot.shares;
            totalPortfolioValue += historicalPriceInDisplayCurrency * lot.shares;
          });
        }
      });

      return {
        date,
        profitLoss: totalProfitLoss,
        portfolioValue: totalPortfolioValue
      };
    });
  }, [stocks, timeRange, currency, exchangeRate]);

  if (stocks.length === 0) return null;

  const is1D = timeRange === '1D';
  const chartData = is1D ? intradayChartData : dailyChartData;

  // Get values based on current view mode
  const values = chartData.map(d => viewMode === 'profit-loss' ? d.profitLoss : d.portfolioValue);
  const minVal = values.length > 0 ? Math.min(...values) : 0;
  const maxVal = values.length > 0 ? Math.max(...values) : 0;
  const padding = maxVal !== minVal ? (maxVal - minVal) * 0.1 : Math.abs(maxVal) * 0.1 || 1;

  // Calculate total invested amount for percentage calculation
  const totalInvested = stocks.reduce((total, stock) => {
    return total + stock.lots.reduce((stockTotal, lot) => {
      const lotPriceInUSD = lot.currency === 'CAD' ? lot.price / exchangeRate : lot.price;
      const lotValueInDisplayCurrency = currency === 'CAD' ? lotPriceInUSD * exchangeRate : lotPriceInUSD;
      return stockTotal + (lotValueInDisplayCurrency * lot.shares);
    }, 0);
  }, 0);

  // Current value and change based on view mode
  const startValue = values.length > 0 ? values[0] : 0;
  const endValue = values.length > 0 ? values[values.length - 1] : 0;
  const change = endValue - startValue;

  const changePercent = viewMode === 'profit-loss'
    ? (totalInvested > 0 ? (endValue / totalInvested) * 100 : 0)
    : (totalInvested > 0 ? (change / totalInvested) * 100 : 0);

  const isPositive = change >= 0;

  const formatTime = (epochMs: number) => {
    const d = new Date(epochMs);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    });
  };

  return (
    <div className="w-full rounded-2xl p-3 sm:p-6 mb-4 sm:mb-8" style={{ backgroundColor: 'var(--color-surface)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-6 gap-2 sm:gap-4">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <h2 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Performance</h2>
            {displayMode === 'chart' && (
              <div className="flex gap-1 rounded-full p-0.5 sm:p-1" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
                <button
                  onClick={() => setViewMode('profit-loss')}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                    viewMode === 'profit-loss'
                      ? 'text-white shadow'
                      : ''
                  }`}
                  style={viewMode === 'profit-loss' ? { backgroundColor: 'var(--color-toggle-active-accent)' } : { color: 'var(--color-text-secondary)' }}
                >
                  P/L
                </button>
                <button
                  onClick={() => setViewMode('portfolio-value')}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                    viewMode === 'portfolio-value'
                      ? 'text-white shadow'
                      : ''
                  }`}
                  style={viewMode === 'portfolio-value' ? { backgroundColor: 'var(--color-toggle-active-accent)' } : { color: 'var(--color-text-secondary)' }}
                >
                  Value
                </button>
              </div>
            )}
            <div className="flex gap-1 rounded-full p-0.5 sm:p-1" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
              <button
                onClick={() => setDisplayMode('chart')}
                className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                  displayMode === 'chart' ? 'text-white shadow' : ''
                }`}
                style={displayMode === 'chart' ? { backgroundColor: 'var(--color-toggle-active-accent)' } : { color: 'var(--color-text-secondary)' }}
              >
                Chart
              </button>
              <button
                onClick={() => setDisplayMode('treemap')}
                className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                  displayMode === 'treemap' ? 'text-white shadow' : ''
                }`}
                style={displayMode === 'treemap' ? { backgroundColor: 'var(--color-toggle-active-accent)' } : { color: 'var(--color-text-secondary)' }}
              >
                Map
              </button>
            </div>
          </div>
          <div className="flex items-baseline gap-1 sm:gap-2">
             <span className={`text-xl sm:text-3xl font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
               {viewMode === 'profit-loss' && (isPositive ? '+' : '')}{currency === 'USD' ? '$' : 'C$'}{formatNumber(Math.abs(endValue), 0)}
             </span>
             <span className={`text-xs sm:text-sm font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                ({changePercent >= 0 ? '+' : ''}{formatNumber(changePercent, 1)}%)
             </span>
             <span className="text-[10px] sm:text-xs uppercase" style={{ color: 'var(--color-text-muted)' }}>{timeRange}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-0.5 sm:gap-1 rounded-full p-0.5 sm:p-1" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
          {(['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', '2Y', '5Y', '10Y'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full transition-all ${
                timeRange === range
                  ? 'shadow'
                  : ''
              }`}
              style={timeRange === range ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[200px] sm:h-[300px] w-full">
        {displayMode === 'treemap' ? (
          <PortfolioTreemap
            stocks={stocks}
            currency={currency}
            exchangeRate={exchangeRate}
            onSelectStock={onSelectStock || (() => {})}
          />
        ) : is1D && isLoadingIntraday ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-accent-subtle)' }} />
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading intraday data...</p>
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 20, bottom: 25 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={isPositive ? '#22c55e' : '#ef4444'} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={isPositive ? '#22c55e' : '#ef4444'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridColor} vertical={false} />
              {is1D ? (
                <XAxis
                  dataKey="date"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  stroke={chartColors.axisColor}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                  tickFormatter={formatTime}
                >
                  <Label value="Time (ET)" offset={-10} position="insideBottom" style={{ fill: chartColors.axisColor, fontSize: '12px', fontWeight: 500 }} />
                </XAxis>
              ) : (
                <XAxis
                  dataKey="date"
                  stroke={chartColors.axisColor}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    if (timeRange === '1W') return `${d.getMonth()+1}/${d.getDate()}`;
                    if (timeRange === '1M' || timeRange === '3M') return `${d.getMonth()+1}/${d.getDate()}`;
                    return `${d.getMonth()+1}/${d.getFullYear().toString().substr(2)}`;
                  }}
                >
                   <Label value="Date" offset={-10} position="insideBottom" style={{ fill: chartColors.axisColor, fontSize: '12px', fontWeight: 500 }} />
                </XAxis>
              )}
              <YAxis
                domain={[minVal - padding, maxVal + padding]}
                stroke={chartColors.axisColor}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(val) => {
                  const sign = viewMode === 'profit-loss' && val >= 0 ? '+' : '';
                  const prefix = currency === 'USD' ? '$' : 'C$';
                  const absVal = Math.abs(val);
                  const range = maxVal - minVal;
                  if (absVal >= 1000) {
                    const kVal = absVal / 1000;
                    const rangeInK = range / 1000;
                    // Use enough decimal places so adjacent ticks produce distinct labels
                    let decimals = 1;
                    if (rangeInK < 1) decimals = 2;
                    if (rangeInK < 0.1) decimals = 3;
                    return `${sign}${prefix}${kVal.toFixed(decimals)}k`;
                  }
                  return `${sign}${prefix}${absVal.toFixed(0)}`;
                }}
                width={85}
              >
                <Label
                   value={viewMode === 'profit-loss' ? `P/L (${currency})` : `Value (${currency})`}
                   angle={-90}
                   position="insideLeft"
                   style={{ fill: chartColors.axisColor, fontSize: '12px', fontWeight: 500, textAnchor: 'middle' }}
                   offset={0}
                 />
              </YAxis>
              <Tooltip
                contentStyle={{ backgroundColor: chartColors.tooltipBg, borderColor: chartColors.tooltipBorder, color: chartColors.textColor, borderRadius: '12px' }}
                itemStyle={{ color: chartColors.textColor }}
                formatter={(value: number | undefined) => {
                  if (value === undefined) return ['--', viewMode === 'profit-loss' ? 'Profit/Loss' : 'Portfolio Value'];
                  const sign = viewMode === 'profit-loss' && value >= 0 ? '+' : '';
                  return [
                    `${sign}${currency === 'USD' ? '$' : 'C$'}${formatNumber(value, 0)}`,
                    viewMode === 'profit-loss' ? 'Profit/Loss' : 'Portfolio Value'
                  ];
                }}
                labelFormatter={(label) => {
                  if (is1D) {
                    const d = new Date(label);
                    return d.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'America/New_York',
                    }) + ' ET';
                  }
                  return new Date(label).toLocaleDateString();
                }}
              />
              <Area
                type="monotone"
                dataKey={viewMode === 'profit-loss' ? 'profitLoss' : 'portfolioValue'}
                stroke={isPositive ? '#22c55e' : '#ef4444'}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorValue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p style={{ color: 'var(--color-text-muted)' }}>Not enough data for this time range.</p>
          </div>
        )}
      </div>
    </div>
  );
};
